import {Component, Input, OnDestroy, OnInit, QueryList, ViewChildren} from '@angular/core';
import {SortEvent, TableHeaderSortableDirective} from '@shared/table-header-sortable/table-header-sortable.directive';
import {
  TriggerQueryEditComponent
} from '@components/administration/machine/machine-configuration/trigger/trigger-query/modals/trigger-query-edit/trigger-query-edit.component';
import {TriggerQuery} from '@models/adminstration/machine/trigger/trigger-query/trigger-query';
import {Trigger} from '@models/adminstration/machine/trigger/trigger';
import {LoadingIndicatorService} from '@services/loading-indicator/loading-indicator.service';
import {TranslateService} from '@ngx-translate/core';
import {NotificationService} from '@services/notification/notification.service';
import {User} from '@models/user/user';
import {Role} from '@models/user/role';
import {TableHeaderSortableService} from '@services/table-header-sortable/table-header-sortable.service';
import {TriggerHelperService} from '@services/trigger/helper/trigger-helper.service';
import {TRIGGER_ACTION_TYPE} from '@constants/trigger/trigger-action-type.constant';
import {DEFAULT_SORT_TYPE} from '@constants/sorting/default-sort-type';
import {takeUntil} from 'rxjs/operators';
import {ReplaySubject} from 'rxjs';
import {ConfirmationDialogService} from '@services/confirmation-dialog.service';
import {
  MachineGroupTriggerQueryService
} from '@services/administration/machine-group/trigger/machine-group-trigger-query.service';
import {ModalService} from '@services/modal/modal.service';
import {OperationType} from '@models/sidebar/menu-notification';

@Component({
    selector: 'scitis-admin-machine-group-trigger-action',
    templateUrl: './admin-machine-group-trigger-action.component.html',
    styleUrls: ['./admin-machine-group-trigger-action.component.scss'],
    standalone: false
})
export class AdminMachineGroupTriggerActionComponent implements OnInit, OnDestroy {

  @Input()
  public machineGroupId: number | undefined;

  @Input()
  public userRole: Role = new Role(new User());

  @ViewChildren(TableHeaderSortableDirective)
  public sortableHeaders: QueryList<TableHeaderSortableDirective> = new QueryList<TableHeaderSortableDirective>();

  public triggerQueriesList: Trigger[] = [];

  private CURRENT_PAGE_NAME = 'TriggerAction';
  private destroy: ReplaySubject<boolean> = new ReplaySubject<boolean>();
  private COMPONENT_NAME: string = 'Trigger Query Edit';
  private deleteTriggerQueryMessageTitle = this.translateService.instant('Delete trigger action');
  private deleteTriggerQueryMessageOK = this.translateService.instant('Delete');
  private deleteTriggerQueryMessageBody = this.translateService.instant('Do you really want to permanently delete the trigger action %s?');

  constructor(
    private modalService: ModalService,
    private loadingIndicator: LoadingIndicatorService,
    private translateService: TranslateService,
    private triggerActionService: MachineGroupTriggerQueryService,
    private notificationService: NotificationService,
    private tableHeaderSortableService: TableHeaderSortableService,
    private triggerHelperService: TriggerHelperService,
    private translation: TranslateService,
    private confirmationDialogService: ConfirmationDialogService,
  ) {
  }

  ngOnInit(): void {
    this.getTriggerQueryList();
  }

  public onTriggerQueryAdd(): void {
    this.getModalRef().then(({parentId, request}) => {
      this.loadingIndicator.show();
      this.triggerActionService.machineGroupTriggerQueryAdd(parentId, request).pipe(takeUntil(this.destroy)).subscribe({
        next: (trigger: Trigger) => {
          this.triggerQueriesList.push(new Trigger(trigger));
          this.onColumnSort(DEFAULT_SORT_TYPE);
          this.loadingIndicator.hide();
          this.notificationService.success(this.getMessage(true, OperationType.ADD), this.COMPONENT_NAME, false);
        },
        error: (err) => {
          this.loadingIndicator.hide();
          this.notificationService.error(this.getMessage(false, OperationType.ADD), err, this.COMPONENT_NAME, false);
        }
      });
    });
  }

  public getBadgeClass(triggerActionType: TRIGGER_ACTION_TYPE): string {
    return this.triggerHelperService.getBadgeClass(triggerActionType);
  }

  public onColumnSort({column, direction}: SortEvent): void {
    this.sortableHeaders = this.tableHeaderSortableService.resetHeaders(this.sortableHeaders, column);
    this.triggerQueriesList = this.tableHeaderSortableService.sortItems<Trigger>(this.triggerQueriesList, column, direction);
  }

  private getTriggerQueryList(): void {
    if (this.machineGroupId) {
      this.loadingIndicator.show(this.translateService.instant('Loading'));
      this.triggerActionService.machineGroupTriggerQueryList(this.machineGroupId).pipe(takeUntil(this.destroy)).subscribe({
        next: (triggerQueries: Trigger[]) => {
          this.triggerQueriesList = triggerQueries;
          this.onColumnSort(DEFAULT_SORT_TYPE);
          this.loadingIndicator.hide();
        },
        error: (error) => {
          this.loadingIndicator.hide();
          const message = this.translateService.instant('TriggerAction. error loading triggerQueries');
          this.notificationService.error(message, error, this.CURRENT_PAGE_NAME, true);
        }
      });
    }
  }

  public onDeleteTrigger(trigger: Trigger): void {
    const deleteMessageBody = this.deleteTriggerQueryMessageBody.replace('%s', trigger.name.toBold());
    this.confirmationDialogService.handleConfirmationDialog(this.deleteTriggerQueryMessageTitle, deleteMessageBody, '',
      this.deleteTriggerQueryMessageOK).subscribe(confirm => {
      if (confirm && this.machineGroupId) {
        this.loadingIndicator.show();
        this.triggerActionService.machineGroupTriggerQueryDelete(this.machineGroupId, trigger.id).pipe(takeUntil(this.destroy)).subscribe({
          next: () => {
            this.triggerQueriesList = this.triggerQueriesList.filter(({id}) => id !== trigger.id);
            this.loadingIndicator.hide();
            this.notificationService.success(this.getMessage(true, OperationType.DELETE), this.COMPONENT_NAME, false);
          },
          error: (err) => {
            this.loadingIndicator.hide();
            this.notificationService.error(this.getMessage(false, OperationType.DELETE), err, this.COMPONENT_NAME, false);
          }
        });
      }
    });
  }

  public onUpdateTrigger($event: Trigger) {
    this.getModalRef($event).then(({parentId, request}) => {
      this.loadingIndicator.show();
      this.triggerActionService.machineGroupTriggerQueryUpdate(parentId, request).pipe(takeUntil(this.destroy)).subscribe({
        next: trigger => {
          const index = this.triggerQueriesList.findIndex(({id}) => $event.id === id);
          this.triggerQueriesList[index] = new Trigger(trigger);
          this.onColumnSort(DEFAULT_SORT_TYPE);
          this.loadingIndicator.hide();
          this.notificationService.success(this.getMessage(true, OperationType.UPDATE), this.COMPONENT_NAME, false);
        },
        error: error => {
          this.loadingIndicator.hide();
          this.notificationService.error(this.getMessage(false, OperationType.UPDATE), error, this.COMPONENT_NAME, true);
        }
      });
    });
  }

  private getModalRef(trigger?: Trigger): Promise<any> {
    const modalRef = this.modalService.open(TriggerQueryEditComponent, {size: 'lg'});
    const parentId = this.machineGroupId || -1;
    modalRef.componentInstance.trigger = new Trigger(trigger);
    modalRef.componentInstance.triggerQuery = new TriggerQuery(parentId);
    modalRef.componentInstance.userRole = this.userRole;
    modalRef.componentInstance.isMachineGroupTrigger = true;
    return modalRef.result;
  }

  private getMessage(success: boolean, operatingType: OperationType): string {
    let message: string = '';
    switch (operatingType) {
      case OperationType.ADD:
        message = success ? 'Successfully created trigger action' : 'Error creating trigger action';
        break;
      case OperationType.DELETE:
        message = success ? 'Successfully deleted trigger action' : 'Error deleting trigger action';
        break;
      case OperationType.UPDATE:
        message = success ? 'Successfully updated trigger action' : 'Error updating trigger action';
    }
    return this.translation.instant(message);
  }

  ngOnDestroy(): void {
    this.destroy.next(true);
    this.destroy.complete();
  }
}
